import React from 'react';
import { useRouter } from 'next/router';
import { gql } from '@soundxyz/gql-string';
import { useAuth } from '@context/auth/base';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  MarkNotificationsAsReadDocument,
  NewGlobalUserNotificationDocument,
  NewUserNotificationDocument,
  useMutation,
  useQuery,
  UserNotificationStatusDocument,
} from '@graphql/reactQuery';
import { useStableCallback } from '@hooks/useStableCallback';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY } from '@utils/constants';
import {
  NotificationStatusPersistence,
  setNotificationStatusPersistence,
  useNotificationState,
} from './store';
import { useUserNotificationSubscriptions } from './useUserNotifications';

/*#__PURE__*/ gql(/* GraphQL */ `
  query UserNotificationStatus {
    userNotificationsStatus {
      id
      hasUnreadNotifications
      lastReadTimestamp
    }
  }

  mutation MarkNotificationsAsRead {
    markAllUserNotificationsRead {
      id
      hasUnreadNotifications
      lastReadTimestamp
    }
  }
`);

RefetchOnComplete({
  trigger: [
    MarkNotificationsAsReadDocument,
    NewUserNotificationDocument,
    NewGlobalUserNotificationDocument,
  ],
  refetch: [UserNotificationStatusDocument],
});

export const useUserNotificationStatus = () => {
  const { isAuthenticatedWithAPI } = useAuth();
  const [hasUnreadNotifications, setHasUnreadNotifications] = React.useState(false);
  const { data, isLoading, refetch } = useQuery(UserNotificationStatusDocument, {
    enabled: isAuthenticatedWithAPI,
    onSuccess: ({ data }) => {
      setHasUnreadNotifications(data.userNotificationsStatus.hasUnreadNotifications);
    },
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });
  const { asPath } = useRouter();

  const { notificationStatus } = useNotificationState();

  const { mutate: mutateNotificationStatus, isLoading: isMarkingNotificationsAsRead } = useMutation(
    MarkNotificationsAsReadDocument,
    {
      onSuccess: ({ data }) => {
        const onNotificationsPage = asPath.includes('/notifications');
        /**
         * update the local state to the BE lastReadTimestamp state when user is on the notifications page
         * to handle the case where if new notifications are received and the user is on the page
         * we don't want to mark them as read yet
         */
        if (data.markAllUserNotificationsRead.lastReadTimestamp && onNotificationsPage) {
          NotificationStatusPersistence.set({
            lastReadTimestamp: data.markAllUserNotificationsRead.lastReadTimestamp,
          });
        }
      },
    },
  );

  const userStatusData = data?.data.userNotificationsStatus;
  const lastReadTimestamp = React.useMemo(() => {
    if (userStatusData?.lastReadTimestamp) {
      return userStatusData.lastReadTimestamp;
    }
    return null;
  }, [userStatusData?.lastReadTimestamp]);

  const previousTimestamp = React.useMemo(() => {
    return (
      notificationStatus.lastReadTimestamp ?? (!!lastReadTimestamp ? lastReadTimestamp - 10 : null)
    );
  }, [lastReadTimestamp, notificationStatus.lastReadTimestamp]);

  /**
   * This function is used to mark all notifications as read on the client side
   * and also updates the lastReadTimestamp in the local storage
   */
  const markNotificationsAsRead = useStableCallback(() => {
    mutateNotificationStatus({});
    refetch();

    if (!!lastReadTimestamp && lastReadTimestamp !== previousTimestamp) {
      setNotificationStatusPersistence({
        lastReadTimestamp: lastReadTimestamp,
      });
    }
  });

  useUserNotificationSubscriptions({
    onNewGlobalUserNotificationSubscribeData: () => {
      refetch();
    },
    onNewUserNotificationSubscribeData: () => {
      refetch();
    },
  });

  return {
    isLoading,
    hasUnreadNotifications,
    lastReadTimestamp,
    previousTimestamp,
    mutateNotificationStatus,
    markNotificationsAsRead,
    isMarkingNotificationsAsRead,
  };
};
